import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, useLocation } from 'react-router-dom';
import LeftNav from '../common/LeftNav';
import TopNav from '../common/TopNav';
import adminCrmService from '../../../services/adminCrmService';
import '../../../assets/styles/PersonalIDs.css';
import '../../assets/styles/CRMOpenLead.css';
import arrowleftt from '../../assets/img/arrowleftt.svg';

// Fields per user role — matches mobile app exactly
const BUILDER_FIELDS = [
  { key: 'pan_number', label: 'PAN Number', maxLength: 10 },
  { key: 'gst_number', label: 'GST Number', maxLength: 15 },
  { key: 'tan_number', label: 'TAN Number', maxLength: 10 },
  { key: 'rera_id',    label: 'RERA ID' },
];

const AGENCY_FIELDS = [
  { key: 'pan_number', label: 'PAN Number', maxLength: 10 },
  { key: 'gst_number', label: 'GST Number', maxLength: 15 },
  { key: 'rera_id',    label: 'RERA ID' },
];

const AGENT_FIELDS = [
  { key: 'pan_number', label: 'PAN Number', maxLength: 10 },
  { key: 'rera_id',    label: 'RERA ID' },
];

const getFieldsForRole = (role) => {
  switch (role) {
    case 'builder': return BUILDER_FIELDS;
    case 'agency':  return AGENCY_FIELDS;
    case 'agent':   return AGENT_FIELDS;
    default:        return AGENCY_FIELDS;
  }
};

export default function AdminCrmProfessionalIds() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId, userName, userRole } = location.state || {};

  const fields = getFieldsForRole(userRole);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});
  const [editedFields, setEditedFields] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => { if (userId) fetchProfile(); }, [userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const res = await adminCrmService.getFullProfile(userId);
      if (res.success && res.data) {
        setProfile(res.data);
        const initial = {};
        fields.forEach(f => { initial[f.key] = res.data[f.key] || ''; });
        setEditedFields(initial);
      }
    } catch { toast.error('Failed to load profile'); }
    finally { setLoading(false); }
  };

  const handleFieldChange = (key, value) => {
    setEditedFields(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const changed = {};
      fields.forEach(f => {
        const original = profile[f.key] || '';
        const edited = editedFields[f.key] || '';
        if (original !== edited) changed[f.key] = edited;
      });
      if (Object.keys(changed).length === 0) {
        toast.info('No fields were modified');
        setSaving(false);
        return;
      }
      const res = await adminCrmService.updateProfile(userId, changed);
      if (res.success) {
        toast.success('Professional IDs updated successfully');
        setHasChanges(false);
        if (res.data) {
          setProfile(res.data);
          const updated = {};
          fields.forEach(f => { updated[f.key] = res.data[f.key] || ''; });
          setEditedFields(updated);
        }
      } else {
        toast.error(res.message || 'Failed to update');
      }
    } catch { toast.error('Failed to update'); }
    finally { setSaving(false); }
  };

  // RERA verification status badge — shown for agency only (matching mobile app)
  const renderReraStatus = () => {
    if (userRole !== 'agency') return null;
    const status = profile.rera_verification_status;
    if (!status) return null;

    if (status === 'verified') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#22C55E' }}>
          ✓ Verified
        </span>
      );
    }
    if (status === 'pending') {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#D97706' }}>
          ⏱ Pending
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <div>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', color: '#DC2626' }}>
            ✕ Rejected
          </span>
          {profile.rera_rejection_reason && (
            <div style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
              Reason: {profile.rera_rejection_reason}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="d-flex" style={{ background: '#fff', minHeight: '100vh' }}>
      <LeftNav />
      <div className="main-content flex-grow-1">
        <TopNav />
        <div className="p-3">

          {/* Header: Back + Title + Save */}
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center gap-2">
              <img src={arrowleftt} onClick={() => navigate(-1)} style={{ cursor: 'pointer' }} alt="back" />
              <h6 className="accordion-title mb-0">Professional IDs</h6>
            </div>
            <button
              onClick={handleSave}
              disabled={!hasChanges || saving}
              style={{
                background: 'none', border: 'none', cursor: hasChanges ? 'pointer' : 'default',
                fontSize: '15px', fontWeight: '600', color: '#2563EB',
                opacity: hasChanges ? 1 : 0.4
              }}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>

          {/* User name + role badge */}
          {userName && (
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#6B7280', marginBottom: '4px' }}>{userName}</p>
          )}
          {userRole && (
            <span style={{
              display: 'inline-block', backgroundColor: '#EEF2FF',
              padding: '3px 10px', borderRadius: '8px', fontSize: '12px',
              fontWeight: '600', color: '#4F46E5', marginBottom: '12px'
            }}>
              {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
            </span>
          )}

          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div><div className="mt-3" style={{ color: '#888' }}>Loading...</div></div>
          ) : (
            <div className="accordion-item mb-3">
              <div className="accordion-header d-flex justify-content-between align-items-center" style={{ cursor: 'default' }}>
                <h6 className="accordion-title mb-0">Professional IDs</h6>
              </div>
              <div className="accordion-body">
                <div className="card p-4 personal-ids-card">

                  {fields.map(field => (
                    <div key={field.key} className="form-group mb-3">
                      <div className="d-flex align-items-center justify-content-between mb-1">
                        <label className="form-label personal-label mb-0">{field.label}</label>
                        {field.key === 'rera_id' && renderReraStatus()}
                      </div>
                      <input
                        type="text"
                        className="form-control personal-input"
                        value={editedFields[field.key] || ''}
                        onChange={e => handleFieldChange(field.key, e.target.value.toUpperCase())}
                        placeholder={`Enter ${field.label}`}
                        maxLength={field.maxLength}
                        style={{ textTransform: 'uppercase' }}
                      />
                    </div>
                  ))}

                  {/* REF ID — read-only for builder & agency (matching mobile app) */}
                  {(userRole === 'builder' || userRole === 'agency') && profile.ref_id && (
                    <div className="form-group mb-3">
                      <label className="form-label personal-label mb-1">REF ID</label>
                      <input
                        type="text"
                        className="form-control personal-input"
                        value={profile.ref_id}
                        readOnly
                        style={{ backgroundColor: '#F3F4F6', color: '#9CA3AF' }}
                      />
                    </div>
                  )}

                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
